import { useQuery } from '@tanstack/react-query'
import { type SelectLocationFull, selectLocationsFullSchema } from '@workspace/db/extra'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'

// Local envelope mirroring GET /v1/locations/:idOrCode ({ data: Location[], success, code, errors }).
// The API returns an ARRAY even for a single id/code; useLocation unwraps `[0]`.
const locationEnvelopeSchema = z.object({
  data: selectLocationsFullSchema,
  success: z.boolean(),
  code: z.union([z.string(), z.number()]).optional(),
  errors: z.unknown().optional(),
})

/** Embeds per manifest (`/locations/:idOrCode` layout) + wan data for the overview. */
export const LOCATION_DETAIL_EMBEDS = [
  'withOrganization',
  'withNodes',
  'withPaymentsProfile',
  'withReservations',
  'withTerminals',
  'withWanData',
] as const

export const locationDetailPath = (idOrCode: string) =>
  `/v1/locations/${encodeURIComponent(idOrCode)}?${LOCATION_DETAIL_EMBEDS.map((e) => `${e}=true`).join('&')}`

/** Pure: the API returns a list; a single-location read wants the first row or null. */
export function pickLocation(rows: SelectLocationFull[]): SelectLocationFull | null {
  return rows[0] ?? null
}

export const locationQueryKey = (idOrCode: string) => ['location', idOrCode] as const

/** Shared location read with a `select` projection (same cache entry as `useLocation`). */
export function useLocationSelect<T>(idOrCode: string, select: (loc: SelectLocationFull | null) => T) {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'location'] : locationQueryKey(idOrCode),
    queryFn: async (): Promise<SelectLocationFull | null> => {
      const res = await api.get(locationDetailPath(idOrCode), locationEnvelopeSchema)
      if (!res.success) throw new Error(`Failed to fetch location (code ${res.code ?? 'unknown'})`)
      return pickLocation(res.data)
    },
    select,
    enabled: !preview && idOrCode.length > 0,
    ...(preview ? { initialData: preview.locationDetail, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

const identity = (loc: SelectLocationFull | null) => loc

/** Single location with org/nodes/payments/reservations/terminals/wan embeds. `data` is `null` when not found. */
export function useLocation(idOrCode: string) {
  return useLocationSelect(idOrCode, identity)
}
